import { useSyncExternalStore } from 'react';
import type { Message } from '../types';
import { upsertMessages } from '../lib/messageMerge';

export interface Feed {
  /** Newest-first. */
  messages: Message[];
  /** Whether the server may have an older page beyond `messages`. */
  hasMore: boolean;
}

const EMPTY: Feed = { messages: [], hasMore: true };

// Per-channel message history as an external store. Messages used to live
// inside each Channel in one React state, so appending a live signal
// re-rendered everything that read `channels`. Here a change notifies only
// the components subscribed to THAT channel's feed (useChannelFeed).
// Snapshots are immutable: every change creates a new Feed object, which is
// what useSyncExternalStore compares.
export class MessageStore {
  private feeds = new Map<string, Feed>();
  private listeners = new Map<string, Set<() => void>>();

  get(channelId: string): Feed {
    return this.feeds.get(channelId) ?? EMPTY;
  }

  messages(channelId: string): Message[] {
    return this.get(channelId).messages;
  }

  hasMore(channelId: string): boolean {
    return this.get(channelId).hasMore;
  }

  subscribe(channelId: string, listener: () => void): () => void {
    let set = this.listeners.get(channelId);
    if (!set) this.listeners.set(channelId, (set = new Set()));
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) this.listeners.delete(channelId);
    };
  }

  private write(channelId: string, feed: Feed) {
    this.feeds.set(channelId, feed);
    this.listeners.get(channelId)?.forEach((l) => l());
  }

  setMessages(channelId: string, messages: Message[]) {
    const cur = this.get(channelId);
    if (cur.messages === messages) return;
    this.write(channelId, { ...cur, messages });
  }

  setHasMore(channelId: string, hasMore: boolean) {
    const cur = this.get(channelId);
    if (cur.hasMore === hasMore && this.feeds.has(channelId)) return;
    this.write(channelId, { ...cur, hasMore });
  }

  /** Upserts and (optionally) caps to the newest `cap`, keeping unsynced optimistic messages. */
  upsert(
    channelId: string,
    incoming: readonly Message[],
    opts: { cap?: number; resolve?: (existing: Message | undefined, incoming: Message) => Message } = {}
  ) {
    let next = upsertMessages(this.messages(channelId), incoming, opts.resolve);
    if (opts.cap !== undefined) next = capMessages(next, opts.cap);
    this.setMessages(channelId, next);
  }

  /** Newest-first cap; returns true if anything was dropped. */
  cap(channelId: string, cap: number): boolean {
    const cur = this.messages(channelId);
    const next = capMessages(cur, cap);
    if (next === cur) return false;
    this.setMessages(channelId, next);
    return true;
  }

  patch(channelId: string, messageId: string, change: (m: Message) => Message) {
    const cur = this.messages(channelId);
    const at = cur.findIndex((m) => m.id === messageId);
    if (at < 0) return;
    const next = cur.slice();
    next[at] = change(cur[at]);
    this.setMessages(channelId, next);
  }

  /** Newest first, matching iOS's insert(at: 0). */
  prepend(channelId: string, message: Message) {
    this.setMessages(channelId, [message, ...this.messages(channelId)]);
  }

  delete(channelId: string) {
    if (!this.feeds.delete(channelId)) return;
    this.listeners.get(channelId)?.forEach((l) => l());
  }

  ids(): string[] {
    return [...this.feeds.keys()];
  }

  clear() {
    for (const id of [...this.feeds.keys()]) this.delete(id);
  }
}

// Keeps the newest `cap` messages, plus any still-unsynced optimistic one.
// Returns the same array when nothing needs dropping.
export function capMessages(messages: Message[], cap: number): Message[] {
  if (messages.length <= cap) return messages;
  return messages.filter((m, i) => i < cap || m.syncStatus);
}

export function useChannelFeedFrom(store: MessageStore, channelId: string | undefined): Feed {
  const id = channelId ?? '';
  return useSyncExternalStore(
    (cb) => store.subscribe(id, cb),
    () => store.get(id),
    () => EMPTY
  );
}
